import { gql, useMutation, useQuery } from '@apollo/client'
import { Button } from '@unpublished/common-components'
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import {
  Breadcrumb,
  ErrorText,
  FixedWidthPageContainer,
  IterationDisplay,
  Table,
} from '../common/common-components'
import { humanizedDurationFromNow } from '../common/data-transforms'
import {
  MeasurementWorkerDetailQuery,
  MeasurementWorkerDetailQueryVariables,
  RerunFailedOrStuckMeasurementInvocationsMutation,
  RerunFailedOrStuckMeasurementInvocationsMutationVariables,
} from '../common/generated'
import { transformMeasurerVersion } from '../common/measurer-version'
import { MeasurerVersionDisplay } from '../common/measurer-version-display'
import { StatusBadge } from '../common/status-badge'
import { useNumericParam } from '../common/use-numeric-param'

const MEASUREMENT_WORKER_DETAIL_QUERY = gql`
  query MeasurementWorkerDetail($commitId: Int!) {
    commitById(id: $commitId) {
      created
      id
      measurerVersionByMeasurerVersionId {
        sha1
        tagName
        stateMachineStateId
        measurerByMeasurerId {
          repoSlug
          name
        }
      }
      measurementInvocationIterationsByMeasurementInvocationCommitId {
        nodes {
          measurementInvocationIteration
          measurementStudyByMeasurementStudyId {
            id
            name
          }
        }
      }
    }
    waitingInvocations: allMeasurementInvocations(
      condition: { commitId: $commitId, stateMachineStateId: "NEW" }
    ) {
      totalCount
    }
    startedInvocations: allMeasurementInvocations(
      condition: { commitId: $commitId, stateMachineStateId: "STARTED" }
    ) {
      totalCount
    }
    succeededInvocations: allMeasurementInvocations(
      condition: { commitId: $commitId, stateMachineStateId: "SUCCESS" }
    ) {
      totalCount
    }
    erroredInvocations: allMeasurementInvocations(
      condition: { commitId: $commitId, stateMachineStateId: "ERROR" }
    ) {
      totalCount
    }
    canceledInvocations: allMeasurementInvocations(
      condition: { commitId: $commitId, stateMachineStateId: "CANCELED" }
    ) {
      totalCount
    }
  }
`

export const RERUN_MUTATION = gql`
  mutation RerunFailedOrStuckMeasurementInvocations(
    $measurementStudyId: Int!
    $measurementInvocationIteration: Int!
  ) {
    rerunFailedOrStuckMeasurementInvocations(
      input: {
        measurementStudyId: $measurementStudyId
        measurementInvocationIteration: $measurementInvocationIteration
      }
    ) {
      integer
    }
  }
`

const StatusTable = styled(Table)`
  border-collapse: collapse;
  display: inline-table;
  margin-top: 0;

  th {
    width: auto;
  }

  th,
  td {
    padding: 8px 24px;
    border-bottom: 1px solid #eee;
    text-align: left;
    vertical-align: middle;
  }

  /* Count column */
  th:nth-child(2),
  td:nth-child(2) {
    text-align: center;
  }

  /* Percentage column */
  th:nth-child(3),
  td:nth-child(3) {
    text-align: right;
  }

  /* Actions column */
  th:nth-child(4),
  td:nth-child(4) {
    text-align: center;
  }

  tr:last-child td {
    border-bottom: none;
  }
`

const MetaSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

function RerunButton({
  studyId,
  iteration,
  label,
}: {
  studyId: number
  iteration: number
  label?: string
}): JSX.Element {
  const [rerun, { data, error, loading }] = useMutation<
    RerunFailedOrStuckMeasurementInvocationsMutation,
    RerunFailedOrStuckMeasurementInvocationsMutationVariables
  >(RERUN_MUTATION, {
    variables: {
      measurementStudyId: studyId,
      measurementInvocationIteration: iteration,
    },
  })

  if (data) {
    return (
      <span>
        Re-running {data.rerunFailedOrStuckMeasurementInvocations?.integer}{' '}
        invocations
        {label ? ` (${label})` : ''}
      </span>
    )
  }
  if (error) {
    return <ErrorText>Error: {error.message}</ErrorText>
  }

  return (
    <Button onClick={() => rerun()} disabled={loading}>
      Re-run{label ? ` (${label})` : ''}
    </Button>
  )
}

export function MeasurementWorkerDetail(): JSX.Element {
  const commitId = useNumericParam('commitId')

  const { loading, error, data } = useQuery<
    MeasurementWorkerDetailQuery,
    MeasurementWorkerDetailQueryVariables
  >(MEASUREMENT_WORKER_DETAIL_QUERY, {
    variables: { commitId },
  })

  const commit = data?.commitById
  const navigate = useNavigate()

  const breadcrumb = (
    <Breadcrumb>
      <Link to="/">Home</Link> {'>'}{' '}
      <Link to="/extras/measurement-work">Recent measurement work</Link> {'>'}{' '}
      Commit {commitId}
    </Breadcrumb>
  )

  if (loading) {
    return (
      <FixedWidthPageContainer>
        {breadcrumb}
        <p>Loading...</p>
      </FixedWidthPageContainer>
    )
  }

  if (error || !commit) {
    return (
      <FixedWidthPageContainer>
        {breadcrumb}
        <ErrorText>
          {error ? error.message : `Commit ${commitId} not found`}
        </ErrorText>
      </FixedWidthPageContainer>
    )
  }

  const measurerVersion = commit.measurerVersionByMeasurerVersionId

  const studyNodes =
    commit.measurementInvocationIterationsByMeasurementInvocationCommitId.nodes.filter(
      n => n.measurementInvocationIteration !== null
    ) as Array<{
      measurementInvocationIteration: number
      measurementStudyByMeasurementStudyId: { id: number; name: string }
    }>

  const multipleStudies = studyNodes.length > 1

  const counts = {
    waiting: data!.waitingInvocations.totalCount,
    started: data!.startedInvocations.totalCount,
    succeeded: data!.succeededInvocations.totalCount,
    errored: data!.erroredInvocations.totalCount,
    canceled: data!.canceledInvocations.totalCount,
  }
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  const pct = (n: number): string =>
    total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '—'

  return (
    <FixedWidthPageContainer>
      {breadcrumb}
      <h1>Commit {commitId}</h1>

      <p>{humanizedDurationFromNow(commit.created)}</p>

      <h2>Measurement study</h2>
      {studyNodes.length === 0 ? (
        <p>N/A</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {studyNodes.map(node => (
            <li
              key={`${node.measurementStudyByMeasurementStudyId.id}-${node.measurementInvocationIteration}`}
            >
              <Link
                to={`/studies/${node.measurementStudyByMeasurementStudyId.id}`}
              >
                {node.measurementStudyByMeasurementStudyId.name}
              </Link>{' '}
              <IterationDisplay
                iteration={node.measurementInvocationIteration}
              />
            </li>
          ))}
        </ul>
      )}

      <h2>Measurer version</h2>
      {measurerVersion ? (
        <MetaSection>
          <MeasurerVersionDisplay
            measurerVersion={transformMeasurerVersion({
              measurerName: measurerVersion.measurerByMeasurerId.name,
              functionName: null,
              repoSlug: measurerVersion.measurerByMeasurerId.repoSlug,
              sha1: measurerVersion.sha1,
              tagName: measurerVersion.tagName,
              commitDate: null,
              commitMessage: null,
            })}
          />
          <StatusBadge state={measurerVersion.stateMachineStateId}>
            {measurerVersion.stateMachineStateId}
          </StatusBadge>
        </MetaSection>
      ) : (
        <p>N/A</p>
      )}

      <h2>Invocation status</h2>
      <StatusTable>
        <thead>
          <tr>
            <th>Status</th>
            <th>Count</th>
            <th>%</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Waiting</td>
            <td>{counts.waiting.toLocaleString()}</td>
            <td>{pct(counts.waiting)}</td>
            <td />
          </tr>
          <tr>
            <td>Started</td>
            <td>{counts.started.toLocaleString()}</td>
            <td>{pct(counts.started)}</td>
            <td>
              {studyNodes.length > 0 && (
                <ActionList>
                  {studyNodes.map(node => (
                    <RerunButton
                      key={`${node.measurementStudyByMeasurementStudyId.id}-${node.measurementInvocationIteration}`}
                      studyId={node.measurementStudyByMeasurementStudyId.id}
                      iteration={node.measurementInvocationIteration}
                      label={
                        multipleStudies
                          ? `${node.measurementStudyByMeasurementStudyId.name} i${node.measurementInvocationIteration}`
                          : undefined
                      }
                    />
                  ))}
                </ActionList>
              )}
            </td>
          </tr>
          <tr>
            <td>Success</td>
            <td>{counts.succeeded.toLocaleString()}</td>
            <td>{pct(counts.succeeded)}</td>
            <td />
          </tr>
          <tr>
            <td>Error</td>
            <td>{counts.errored.toLocaleString()}</td>
            <td>{pct(counts.errored)}</td>
            <td>
              {studyNodes.length > 0 && (
                <ActionList>
                  {studyNodes.map(node => (
                    <Button
                      key={`${node.measurementStudyByMeasurementStudyId.id}-${node.measurementInvocationIteration}`}
                      onClick={() =>
                        navigate(
                          `/studies/${node.measurementStudyByMeasurementStudyId.id}/errors/${node.measurementInvocationIteration}`
                        )
                      }
                    >
                      Errors
                      {multipleStudies
                        ? ` (${node.measurementStudyByMeasurementStudyId.name} i${node.measurementInvocationIteration})`
                        : ''}
                    </Button>
                  ))}
                </ActionList>
              )}
            </td>
          </tr>
          <tr>
            <td>Canceled</td>
            <td>{counts.canceled.toLocaleString()}</td>
            <td>{pct(counts.canceled)}</td>
            <td />
          </tr>
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td>
              <strong>{total.toLocaleString()}</strong>
            </td>
            <td />
            <td />
          </tr>
        </tbody>
      </StatusTable>
    </FixedWidthPageContainer>
  )
}
